import axios from "axios";
import { isEmpty } from "lodash";
import { stringify } from "qs";
import moment from "moment-timezone";
import { parse } from "date-fns";
import { brand } from "src/brand";

export const api = (
	path: string,
	version = "",
	method = "GET",
	payload = {},
	params?: any,
	token = "",
	header = "json",
	responseType = ""
) => {
	let endpoint = import.meta.env.API_URL;
	switch (version) {
		case "User":
			endpoint += "fmsuser";
			break;
		case "Vehicle":
			endpoint += "vehicle";
			break;
		case "Tasking":
			endpoint += "fmstasking";
			break;
		case "CMM":
			endpoint = import.meta.env.CMM_API_URL;
			break;
		case "evCharging":
			endpoint += "evCharging";
			break;
		case "Booking":
			endpoint += "booking";
			break;
		case "vsr":
			endpoint += "vsr";
			break;
		case "Internal":
			endpoint += "internal";
			break;
		case "Telematics":
			endpoint += "telematics";
			break;
		default:
			endpoint = "";
	}

	const options: any = {
		url: endpoint + path,
		method: method.toUpperCase(),
		headers: {},
	};

	const needBasicAuth = "NEED_BASIC_AUTH";
	const auth_username = import.meta.env.AUTH_USER_NAME;
	const auth_password = import.meta.env.AUTH_PASSWORD;

	if (token) {
		options["headers"]["Authorization"] = "Bearer " + token;
	}

	if (!token && needBasicAuth) {
		options["auth"] = {
			username: auth_username,
			password: auth_password,
		};
	}

	if (responseType) {
		options["responseType"] = responseType;
	}

	if (!isEmpty(payload) || Object.entries(payload)) {
		options["data"] = payload;
	}

	if (!isEmpty(params)) {
		options["url"] =
			options["url"] + stringify(params, { addQueryPrefix: true });
	}

	switch (header) {
		// if multipart ignore the option headers
		case "multipart":
			options["headers"]["Content-Type"] = "multipart/form-data";
			break;
		case "csv":
			options["headers"]["Accept"] = "application/csv";
			options["responseType"] = "blob";
			break;
		default:
			options["headers"]["Content-Type"] = "application/json";
	}
	return axios(options);
};

export const queryParams = () => {
	let params = new URLSearchParams(window.location.search);
	const entries = params.entries();
	const result = {};
	for (const [key, value] of entries) {
		// each 'entry' is a [key, value] tupple
		result[key] = value;
	}
	return result;
};

export const listQueryParams = (query) => {
	let obj = {};
	for (let key in query) {
		if (
			key !== "mobileNumber" &&
			key !== "userMobileNumber" &&
			key !== "jobReferenceId" &&
			key !== "vehicleNumber" &&
			key !== "region"
		) {
			if (
				!Number.isNaN(Number.parseFloat(query[key])) &&
				Number.isFinite(Number(query[key]))
			) {
				obj[key] = Number.parseInt(query[key]);
			} else {
				obj[key] = query[key];
			}
		} else {
			obj[key] = query[key];
		}
	}
	return obj;
};

//Convert time to Singapore local time
export const convert = (time: string, format = "DD MMM YYYY LT") => {
	if (!time) return "";
	if (!format) format = "DD MMM YYYY LT";
	let eventStartTime = moment.utc(time).tz(brand.timezone).format(format);
	return eventStartTime;
};

//Convert time to Singapore local date
export const convertDateOnly = (time, format = "DD MMM YYYY") => {
	if (!time) return "";
	let eventStartTime = moment.utc(time).tz(brand.timezone).format(format);
	return eventStartTime;
};

//Convert time to Singapore local date
export const convertDateTime = (time, format = "YYYY-MM-DD HH:mm") => {
	let eventStartTime = moment.utc(time).tz(brand.timezone).format(format);
	return eventStartTime;
};

export const convertSGDateOnly = (time) => {
	let eventStartTime = moment
		.utc(time)
		.tz(brand.timezone)
		.format("YYYY-MM-DD");
	return eventStartTime;
};

// Converts a date-only string (interpreted as local midnight in
// brand.timezone) into the equivalent UTC Date instance - e.g. for
// Asia/Singapore, "2026-10-10" -> 2026-10-09T16:00:00.000Z.
export const convertLocalDateOnlyToUtc = (dateOnly) => {
	if (!dateOnly) return null;
	return moment.tz(dateOnly, brand.timezone).startOf("day").toDate();
};

//Convert time to Singapore local time
export const convertTimeOnly = (time) => {
	let eventStartTime = moment.utc(time).tz(brand.timezone).format("LT");
	return eventStartTime;
};
//Convert to UTC
export const convertDateToUtc = (time) => {
	let eventStartTime = moment.utc(time).format("YYYY-MM-DD HH:mm");
	return eventStartTime;
};

export const convertTimeFormatOnly = (time, format = "YYYY-MM-DD HH:mm") => {
	if (time) {
		let eventStartTime = moment.utc(time).format(format);
		return eventStartTime;
	}
	return "";
};

export const sveltyPickerFormat = "dd M yyyy H:ii P";
export const momentFormat = "DD MMM YYYY h:mm A";
export const pickerFormat = "dd MMM yyyy h:mm a";

export const convertToUtc = (datetime, format = pickerFormat) => {
	if (!datetime) return "";

	let parsedDate = parse(datetime, format, new Date());
	let dateTimeObj = new Date(parsedDate);
	return moment(dateTimeObj).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

export const img_endpoint = import.meta.env.IMG_API_URL;

export const formatSummary = (summary) => {
	let words = summary.split(", ");
	words.forEach((word, index) => {
		words[index] = word.charAt(0).toUpperCase() + word.slice(1);
		words[index] = words[index].replace("_", " ");
	});
	return words.join(", ");
};

export const joinErrorMessages = (data, errorCode = null) => {
	let errorMessages = [];

	for (const key in data) {
		if (data.hasOwnProperty(key)) {
			const messages = data[key];
			if (Array.isArray(messages)) {
				errorMessages = errorMessages.concat(messages);
			}
		}
	}

	return `Error ${errorCode}: ` + errorMessages.join(" ");
};

export const filterParams = (params) => {
	const filteredParams = {};
	for (const key in params) {
		if (params[key] !== "") {
			filteredParams[key] = params[key];
		}
	}
	return filteredParams;
};

export function timeElapsed(datetimeUTC) {
	// Parse the input datetime string into a Date object
	const inputDate = new Date(datetimeUTC);
	if (Number.isNaN(inputDate.getTime())) {
		throw new TypeError(
			"Invalid date format. Please provide a valid UTC datetime."
		);
	}

	// Get the current date
	const currentDate = new Date();

	// Calculate the difference in years, months, and days
	let years = currentDate.getUTCFullYear() - inputDate.getUTCFullYear();
	let months = currentDate.getUTCMonth() - inputDate.getUTCMonth();
	let days = currentDate.getUTCDate() - inputDate.getUTCDate();

	// Adjust the months and years if necessary
	if (days < 0) {
		months -= 1;
		const prevMonth = new Date(
			currentDate.getUTCFullYear(),
			currentDate.getUTCMonth(),
			0
		);
		days += prevMonth.getUTCDate();
	}
	if (months < 0) {
		years -= 1;
		months += 12;
	}

	// Total months elapsed
	const totalMonths = years * 12 + months;

	if (totalMonths > 0) {
		return `${totalMonths} months`;
	}
	if (days > 0) {
		return `${days} days`;
	}
	return "Today";
}
